import { z } from 'zod';

/**
 * Single source of truth for every `import.meta.env` read the web app
 * makes. Zod parses once at module load and caches the result, so a
 * missing or malformed value surfaces at boot rather than the first
 * request that happens to touch it.
 *
 * `VITE_API_ORIGIN` is required: an empty string would silently make
 * fetches relative to the SPA origin (i.e. talk to the SPA itself).
 * `VITE_SENTRY_*` are optional — the Sentry init is a no-op when DSN is
 * absent.
 */

const EnvSchema = z.object({
  apiOrigin: z.url(),
  sentry: z.object({
    dsn: z.string().min(1).optional(),
    release: z.string().min(1).optional(),
    // 0..1, parsed downstream by the Sentry helper.
    tracesSampleRate: z.string().min(1).optional(),
  }),
  googleMapsApiKey: z.string().min(1).optional(),
  /**
   * Set (`VITE_SANDBOX=true`) only by the hosted prod-data sandbox build, which
   * shows a persistent banner. Anything but the literal `true` is off.
   */
  sandbox: z.boolean(),
  mode: z.string().min(1),
});

export type Env = z.infer<typeof EnvSchema>;

function readRawValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  return value.length > 0 ? value : undefined;
}

function parse(): Env {
  try {
    return EnvSchema.parse({
      apiOrigin: readRawValue(import.meta.env['VITE_API_ORIGIN']),
      sentry: {
        dsn: readRawValue(import.meta.env['VITE_SENTRY_DSN']),
        release: readRawValue(import.meta.env['VITE_SENTRY_RELEASE']),
        tracesSampleRate: readRawValue(import.meta.env['VITE_SENTRY_TRACE_RATE']),
      },
      googleMapsApiKey: readRawValue(import.meta.env['VITE_GOOGLE_MAPS_API_KEY']),
      sandbox: import.meta.env['VITE_SANDBOX'] === 'true',
      mode: import.meta.env.MODE,
    });
  } catch (err) {
    if (err instanceof z.ZodError) {
      const summary = err.issues.map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`).join('\n');
      // Fail loudly. Without this the SPA boots with broken
      // configuration and the failure shows up much later in a
      // confusing place.
      throw new Error(`Invalid web-app environment configuration:\n${summary}`);
    }

    throw err;
  }
}

export const env: Env = parse();
